import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { openCookiePreferences } from "@shared/lib/cookieConsent";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Check,
  Crown,
  GraduationCap,
  Lightning,
  Plus,
  ShoppingCart,
  Storefront,
  Tag,
  UsersThree,
  X,
} from "@phosphor-icons/react";
import { SectionHeading } from "../components/landing/SectionHeading";
import { SuuperPriceCard } from "../components/landing/SuuperPriceCard";
import { GroupUnlockCard } from "../components/landing/GroupUnlockCard";
import { WaitlistForm } from "../components/landing/WaitlistForm";
import { OfferWheel } from "../components/landing/OfferWheel";
import { RevealImage } from "../components/landing/RevealImage";
import { brandify } from "../components/brand";
import { RevealText, Reveal } from "../components/landing/RevealText";
import { PhotoMarquee } from "../components/landing/PhotoMarquee";
import { SuuperPaySection } from "../components/landing/SuuperPaySection";
import type { LandingImageKey } from "../components/landing/landingImages";
import { articles, formatArticleDate } from "../content/noutati";
import heroCopy from "../../../../content/heroCopy.json";
import {
  demoOffers,
  formatNumber,
  heroOffer,
  offerSaving,
  type CategoryKey,
} from "../components/landing/demoData";

const PARTNER_EMAIL = "parteneri@suuper.ro";
const SUPPORT_EMAIL = "support@suuper.ro";

type JoinSearch = {
  ref?: string;
  /** Comutatorul din top: implicit e înscrierea, `detalii` arată tot clubul. */
  vezi?: "detalii";
};

export const Route = createFileRoute("/join")({
  validateSearch: (search: Record<string, unknown>): JoinSearch => ({
    ref: typeof search.ref === "string" ? search.ref.slice(0, 12) : undefined,
    vezi: search.vezi === "detalii" ? "detalii" : undefined,
  }),
  head: () => ({
    meta: [
      { title: "suuper — Prețuri de membru la ce cumperi oricum" },
      {
        name: "description",
        content:
          "Clubul în care ne strângem mai mulți ca să plătim mai puțin. Înscrie-te acum: primii 1.000 primesc pachetul de fondator.",
      },
      {
        property: "og:title",
        content: "Prețuri de membru la ce cumperi oricum.",
      },
      {
        property: "og:description",
        content:
          "Primii 1.000 de înscriși primesc pachetul de fondator: statut permanent, acces înaintea tuturor, gratuit în perioada de lansare.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://suuper.ro/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: "Prețuri de membru la ce cumperi oricum.",
      },
      {
        name: "twitter:description",
        content:
          "Primii 1.000 de înscriși primesc pachetul de fondator.",
      },
    ],
  }),
  component: JoinPage,
});

/** Toate categoriile primesc propria scenă în grila bento. */
const CATEGORY_TILES: Array<{
  key: CategoryKey;
  label: string;
  image: LandingImageKey;
  className: string;
  imageClassName: string;
}> = [
  {
    key: "supermarket",
    label: "Supermarket",
    image: "category-supermarket",
    className: "col-span-2 row-span-2",
    imageClassName: "object-center",
  },
  {
    key: "fashion",
    label: "Modă",
    image: "category-fashion",
    className: "col-span-2",
    imageClassName: "object-[center_18%]",
  },
  {
    key: "travel",
    label: "Călătorii",
    image: "category-travel",
    className: "col-span-2",
    imageClassName: "object-[center_22%]",
  },
  {
    key: "food",
    label: "Mâncare",
    image: "category-food",
    className: "col-span-2 sm:col-span-1",
    imageClassName: "object-center",
  },
  {
    key: "cinema",
    label: "Cinema",
    image: "category-cinema",
    className: "col-span-2",
    imageClassName: "object-[center_18%]",
  },
  {
    key: "fitness",
    label: "Fitness",
    image: "category-fitness",
    className: "col-span-2 sm:col-span-1",
    imageClassName: "object-center",
  },
  {
    key: "gaming",
    label: "Jocuri",
    image: "category-gaming",
    className: "col-span-2",
    imageClassName: "object-[center_36%]",
  },
  {
    key: "telecom",
    label: "Telecom",
    image: "category-telecom",
    className: "col-span-2",
    imageClassName: "object-[center_38%]",
  },
];

const MARQUEE_IMAGES: LandingImageKey[] = [
  "man-phone-sunny-street",
  "women-walking-shopping-street",
  "skater-colorful-street",
  "couple-phone-red-sweater",
  "man-coffee-walking-city",
  "women-cafe-terrace-coffee",
  "couple-laughing-street",
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Intri",
    text: "Îți faci contul gratuit în mai puțin de un minut. Dacă ești student, îți verificăm statutul.",
  },
  {
    step: "02",
    title: "Unde-s mulți, puterea crește",
    text: "Cu cât suntem mai mulți, cu atât obținem prețuri mai bune de la magazine și branduri.",
  },
  {
    step: "03",
    title: "Plătești mai puțin",
    text: "Vezi prețul direct în aplicație și îl folosești. Plătești cu RoPay din banca ta, iar vizita se adună în recompense.",
  },
];

const SIGNUP_PROMISES = [
  "Acces gratuit în perioada de lansare",
  "Fără card, fără obligații",
  "Te retragi oricând, direct din email",
];

const FAQ_ITEMS = [
  {
    question: "Ce este, mai exact, suuper?",
    answer:
      "suuper este o comunitate de cumpărare. Adunăm interesul mai multor oameni și îl transformăm în putere de negociere pentru produse și experiențe pe care membrii deja le folosesc.",
  },
  {
    question: "Ce nu este suuper?",
    answer:
      "Nu este un site de cupoane, un comparator de prețuri sau o listă de promoții publice. Și nu este un sistem de recrutare: nu câștigi bani din invitați și nu trebuie să vinzi nimic nimănui. Când negociem un preț pentru comunitate, îl vezi direct în aplicație.",
  },
  {
    question: "Cum ajunge comunitatea la un preț mai bun?",
    answer:
      "Mai mulți membri înseamnă o cerere mai relevantă pentru parteneri. suuper folosește acest volum în discuțiile comerciale, iar unele prețuri se pot îmbunătăți când comunitatea atinge un prag anunțat clar.",
  },
  {
    question: "Exemplele de preț de pe pagină sunt oferte reale?",
    answer:
      "Nu. Sunt exemple demonstrative, folosite ca să arate cum va funcționa experiența. O ofertă devine disponibilă numai după activarea parteneriatului și apare în aplicație cu prețul și condițiile sale reale.",
  },
  {
    question: "Este gratuit? Trebuie să introduc un card?",
    answer:
      "Înscrierea în club și accesul în perioada de lansare sunt gratuite și nu cer datele cardului. Dacă modelul sau condițiile se schimbă ulterior, le comunicăm înainte ca tu să alegi dacă vrei să continui.",
  },
  {
    question: "suuper este doar pentru studenți?",
    answer:
      "Nu. suuper este pentru oricine vrea să facă parte din comunitate. Studenții își pot verifica statutul pentru a vedea și prețurile sau pragurile create special pentru ei.",
  },
  {
    question: "Cum plătesc la comerciant? Trec banii prin suuper?",
    answer:
      "Nu. Plătești prin RoPay, serviciul național de plăți instant: scanezi QR-ul suuper de la casă cu aplicația băncii tale și confirmi acolo. Banii pleacă din contul tău direct în contul comerciantului, în câteva secunde, fără comision pentru tine. suuper primește doar confirmarea plății și îți dă chitanța și vizita în programul de recompense.",
  },
  {
    question: "Ce înseamnă recompensele la vizite?",
    answer:
      "Unii comercianți parteneri au un program simplu: la un număr de vizite plătite prin suuper, primești ceva din partea casei. Nu sunt puncte, nu sunt niveluri, nu expiră după reguli complicate. Vezi în aplicație câte vizite mai ai până la recompensă.",
  },
  {
    question: "Ce se întâmplă după ce intru în club?",
    answer:
      "Îți păstrăm locul pentru accesul înainte de lansare și îți scriem când aplicația se deschide sau când apare un preț relevant pentru comunitate. Fără card și fără obligația de a cumpăra.",
  },
] as const;

// ============================================
// PACHETUL DE FONDATOR
// ============================================
// Astea sunt promisiuni de business, nu detalii tehnice: tot ce scrie aici
// trebuie onorat la lansare. Se editează într-un singur loc, ca lista de pe
// pagină și ce comunicăm în rest să nu poată diverge.
const FOUNDER_CAP = 1000;

const FOUNDER_PERKS = [
  {
    icon: Crown,
    title: "Statut de fondator, permanent",
    text: "Ești marcat ca membru fondator în aplicație, de la prima zi. Nu expiră.",
  },
  {
    icon: Lightning,
    title: "Intri înaintea tuturor",
    text: "Primești acces la aplicație înaintea lansării publice și vezi primele prețuri negociate.",
  },
  {
    icon: Tag,
    title: "Gratuit în perioada de lansare",
    text: "Fondatorii nu plătesc abonament cât durează perioada de lansare.",
  },
  {
    icon: UsersThree,
    title: "Prioritate la ofertele limitate",
    text: "Când o ofertă are locuri puține, fondatorii sunt primii care le pot folosi.",
  },
] as const;

/**
 * Comutatorul din top: aceeași rută, două vizualizări. Implicit e înscrierea —
 * scopul paginii — iar „Detalii" arată prezentarea completă a clubului.
 * Starea trăiește în URL, deci se poate da mai departe ca link și
 * supraviețuiește unui refresh.
 */
function ViewSwitcher({ activ }: { activ: "inscriere" | "detalii" }) {
  const pastila = (esteActiv: boolean) =>
    `rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
      esteActiv
        ? "bg-white text-text shadow-sm"
        : "text-text-light hover:text-text"
    }`;

  return (
    <div
      className="flex items-center gap-0.5 rounded-full border border-border bg-white/60 p-1 backdrop-blur-sm"
      role="group"
      aria-label="Alege vizualizarea"
    >
      <Link
        to="/join"
        search={(prev) => ({ ...prev, vezi: undefined })}
        className={pastila(activ === "inscriere")}
        aria-current={activ === "inscriere" ? "page" : undefined}
      >
        Înscriere
      </Link>
      <Link
        to="/join"
        search={(prev) => ({ ...prev, vezi: "detalii" as const })}
        className={pastila(activ === "detalii")}
        aria-current={activ === "detalii" ? "page" : undefined}
      >
        Detalii
      </Link>
    </div>
  );
}

/**
 * Vizualizarea implicită: anunțul de lansare și captarea de email, nimic
 * altceva. Conținutul de aici e și cel prerandat în HTML (heroCopy.json),
 * deci heroul nu are animații de intrare — ce e deja pe ecran nu poate porni
 * din opacity: 0 fără să clipească la preluarea de către React.
 */
function InscriereView() {
  const { ref } = Route.useSearch();
  const stats = useQuery(api.waitlist.getPublicStats);
  const founderCap = stats?.founderCap ?? FOUNDER_CAP;
  const locuriOcupate = Math.min(stats?.members ?? 0, founderCap);

  return (
    <div className="join-page relative flex min-h-screen flex-col bg-white">
      {/* Gradientul acoperă și meniul, nu doar heroul: altfel navbarul stătea
          pe o bandă albă, ruptă vizual de restul capului de pagină. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[560px] bg-gradient-to-b from-primary/8 via-primary/4 to-transparent"
      />

      {/* ============ NAVBAR ============ */}
      <header className="relative w-full">
        <nav
          className="container mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-4"
          aria-label="Navigație principală"
        >
          <img
            src="/suuper_logo.svg"
            alt="suuper"
            className="h-6 w-auto sm:h-7"
          />

          <div className="flex items-center gap-2">
            <ViewSwitcher activ="inscriere" />
            <Link
              to="/pentru-business"
              className="hidden sm:inline-flex px-3 py-2 text-sm font-medium text-text-light hover:text-text transition-colors"
            >
              Pentru business
            </Link>
          </div>
        </nav>
      </header>

      <main className="flex-1">
        {/* ============ ANUNȚUL + FORMULARUL ============ */}
        <section className="relative px-4 pt-14 pb-16 sm:pt-20 sm:pb-20">
          <div className="container relative mx-auto max-w-2xl text-center">
            <h1
              className="font-bold text-text tracking-tight leading-[1.04]"
              style={{ fontSize: "clamp(2.5rem, 7vw, 4.25rem)" }}
            >
              {brandify(heroCopy.titleLine1)}
              <br />
              {heroCopy.titleLine2}
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-text-light sm:text-xl">
              {brandify(heroCopy.subtitle)}
            </p>

            <div className="mx-auto mt-9 max-w-[440px] text-left">
              <WaitlistForm
                source="landing-founder"
                chrome="bare"
                compact
                submitLabel="Devin membru fondator"
                referredBy={ref}
              />
            </div>

            {stats && stats.members > 0 && (
              <p className="mt-6 text-sm text-text-muted">
                <span className="font-semibold text-text">
                  {formatNumber(locuriOcupate)}
                </span>{" "}
                din {formatNumber(founderCap)} locuri de fondator ocupate
              </p>
            )}

          </div>
        </section>

        {/* Exemplele concrete, nu fotografii de atmosferă: primul test de
            utilizator n-a înțeles din hero ce e produsul, iar un preț tăiat
            comunică modelul într-o secundă. Roata le arată pe toate. */}
        <OfferWheel />

        {/* Plata prin RoPay și recompensele: a doua jumătate a produsului,
            spusă scurt. Detaliile stau în vizualizarea „Detalii". */}
        <SuuperPaySection compact />

        {/* ============ PACHETUL DE FONDATOR ============ */}
        <section className="px-4 pb-20 sm:pb-24">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-center text-2xl font-bold tracking-tight text-text sm:text-3xl">
              Pachetul de fondator
            </h2>
            <p className="mx-auto mt-3 max-w-md text-center text-text-light">
              Primii {formatNumber(FOUNDER_CAP)} de înscriși nu așteaptă doar
              lansarea. Pornesc cu un avantaj.
            </p>

            <ul className="mt-10 grid gap-4 sm:grid-cols-2">
              {FOUNDER_PERKS.map((perk) => (
                <li
                  key={perk.title}
                  className="flex items-start gap-4 rounded-3xl border border-border bg-white p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <perk.icon size={22} weight="fill" />
                  </span>
                  <span>
                    <span className="block font-bold text-text">
                      {perk.title}
                    </span>
                    <span className="mt-1 block text-sm leading-relaxed text-text-light">
                      {perk.text}
                    </span>
                  </span>
                </li>
              ))}
            </ul>

            {/* Diferențierea de „cluburile" MLM se spune explicit, nu se
                subînțelege: Lyoness/myWorld a ars exact acest public. Nimeni
                nu câștigă bani din recrutare aici — invitațiile apropie doar
                comunitatea de praguri, pentru toți. */}
            <p className="mx-auto mt-8 max-w-lg text-center text-sm leading-relaxed text-text-muted">
              suuper nu e un sistem de recrutare: nu câștigi bani din invitați
              și nu vinzi nimic nimănui. Cu cât suntem mai mulți, cu atât
              negociem prețuri mai bune — pentru toți, la fel.
            </p>

            <p className="mt-8 text-center text-text-light">
              Vrei să vezi cum funcționează clubul?{" "}
              <Link
                to="/join"
                search={(prev) => ({ ...prev, vezi: "detalii" as const })}
                className="font-semibold text-primary hover:underline"
              >
                Vezi detaliile →
              </Link>
            </p>
          </div>
        </section>
      </main>

      {/* ============ FOOTER ============ */}
      <footer className="rounded-t-[32px] bg-primary px-4 py-10 sm:rounded-t-[44px]">
        <div className="container mx-auto flex max-w-4xl flex-col items-center justify-between gap-5 md:flex-row">
          <img
            src="/suuper_logo_white.svg"
            alt="suuper"
            className="h-6 w-auto"
          />

          <nav
            className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-white/85"
            aria-label="Linkuri footer"
          >
            <Link to="/pentru-business" className="transition-colors hover:text-white">
              Pentru business
            </Link>
            <Link to="/noutati" className="transition-colors hover:text-white">
              Noutăți
            </Link>
            <Link to="/confidentialitate" className="transition-colors hover:text-white">
              Confidențialitate
            </Link>
            <Link to="/termeni" className="transition-colors hover:text-white">
              Termeni
            </Link>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="transition-colors hover:text-white"
            >
              Setări cookie-uri
            </button>
            <span className="text-white/80">© suuper</span>
          </nav>
        </div>
      </footer>
    </div>
  );
}

/** Ruta alege vizualizarea; comutatorul din top o schimbă prin URL. */
function JoinPage() {
  const { vezi } = Route.useSearch();
  return vezi === "detalii" ? <DetaliiView /> : <InscriereView />;
}

function DetaliiView() {
  const { ref } = Route.useSearch();
  const reduceMotion = useReducedMotion();
  const stats = useQuery(api.waitlist.getPublicStats);

  const [isScrolled, setIsScrolled] = useState(false);
  const [isStudent, setIsStudent] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSignup = useCallback(
    (options?: { asStudent?: boolean }) => {
      if (options?.asStudent) setIsStudent(true);
      document.getElementById("signup")?.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "start",
      });
    },
    [reduceMotion]
  );

  return (
    <div className="join-page min-h-screen bg-white">
      {/* ============ NAVBAR ============ */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/85 backdrop-blur-md rounded-b-[28px] sm:rounded-b-[36px]"
            : "bg-transparent"
        }`}
      >
        <nav
          className="container mx-auto max-w-6xl px-4 py-3.5 flex items-center justify-between gap-4"
          aria-label="Navigație principală"
        >
          <a href="#top" className="flex items-center shrink-0">
            <img
              src="/suuper_logo.svg"
              alt="suuper"
              className="h-6 sm:h-7 w-auto"
            />
          </a>

          <div className="flex items-center gap-1 sm:gap-2">
            <ViewSwitcher activ="detalii" />
            <a
              href="#cum-functioneaza"
              className="hidden md:inline-flex px-3 py-2 text-sm font-medium text-text-light hover:text-text transition-colors"
            >
              Cum funcționează
            </a>
            <Link
              to="/pentru-business"
              className="hidden md:inline-flex px-3 py-2 text-sm font-medium text-text-light hover:text-text transition-colors"
            >
              Pentru business
            </Link>
            <Link
              to="/noutati"
              className="hidden md:inline-flex px-3 py-2 text-sm font-medium text-text-light hover:text-text transition-colors"
            >
              Noutăți
            </Link>
            <button
              type="button"
              onClick={() => scrollToSignup()}
              className="join-cta px-4 py-2.5 text-sm font-semibold bg-primary-gradient text-white rounded-xl whitespace-nowrap"
            >
              Intră în suuper club
            </button>
          </div>
        </nav>
      </header>

      {/* ============ HERO ============ */}
      <section id="top" className="relative pt-28 pb-16 sm:pt-36 sm:pb-24 px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/8 via-white to-white pointer-events-none" />

        <div className="container mx-auto max-w-6xl relative">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-16 items-center">
            {/* Heroul NU are animație de intrare, spre deosebire de restul
                paginii, și e intenționat.
                
                Conținutul lui pleacă prerandat în HTML, deci e pe ecran înainte
                să ruleze vreun JavaScript. O animație care pornește din
                opacity: 0 l-ar face să dispară în momentul în care React preia
                pagina, ca să reapară o jumătate de secundă mai târziu — pe o
                conexiune reală, unde bundle-ul vine în câteva sute de ms, asta
                se vede ca o clipire.
                
                Conținutul prerandat și animațiile de intrare se exclud reciproc
                pe același element. Am ales conținutul: heroul e primul lucru de
                citit, nu are ce să „reveleze". Restul paginii, care oricum
                apare la scroll, își păstrează animațiile. */}
            <div className="text-center lg:text-left">
              {/* Titlul spune ce câștigă cititorul. Mecanismul („suntem mai
                  mulți, deci negociem") stă în subtitlu, ca justificare. */}
              <h1
                className="font-bold text-text tracking-tight leading-[1.03] mb-6"
                style={{ fontSize: "clamp(2.5rem, 6vw, 4rem)" }}
              >
                Plătești mai puțin
                <br />
                pentru ce cumperi oricum.
              </h1>

              <p className="text-lg sm:text-xl text-text-light leading-relaxed mb-9 max-w-xl mx-auto lg:mx-0">
                Ne strângem mai mulți și negociem prețuri pe care nu le-ai
                obține singur — la cumpărături, mâncare, travel și
                entertainment.
              </p>

              {/* Formularul stă direct în hero: cel mai scurt drum de la titlu
                  la înscriere. Cel din secțiunea de mai jos rămâne, pentru cine
                  ajunge acolo după ce a citit pagina. */}
              <div className="mx-auto max-w-[520px] text-left lg:mx-0">
                <WaitlistForm
                  source="landing-hero"
                  chrome="bare"
                  isStudent={isStudent}
                  onIsStudentChange={setIsStudent}
                  referredBy={ref}
                />
              </div>
            </div>

            {/* Foto + cardul de preț suprapus peste colț */}
            {/* Și fotografia e prerandată, deci fără animație de intrare —
                același motiv ca la textul de alături. */}
            <div className="relative mx-auto w-full max-w-[420px] lg:max-w-none pb-24 sm:pb-28 lg:pb-32">
              <div
                aria-hidden="true"
                className="absolute -inset-6 rounded-[3rem] bg-primary/15 blur-3xl"
              />

              <RevealImage
                name="friends-phone-reaction-city"
                priority
                ratio="4/5"
                sizes="(max-width: 1024px) 90vw, 480px"
                className="relative rounded-[32px] shadow-2xl"
              />

              <div className="absolute -bottom-2 left-0 right-0 sm:-bottom-1 lg:-left-10 lg:right-auto lg:w-[340px] flex justify-center lg:block">
                <SuuperPriceCard
                  offer={heroOffer}
                  caption={
                    stats && stats.members > 0
                      ? `${formatNumber(stats.members)} ${
                          stats.members === 1
                            ? "membru înscris"
                            : "membri înscriși"
                        } până acum`
                      : undefined
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CATEGORII - BENTO ============ */}
      <section className="py-16 sm:py-24 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mx-auto max-w-3xl mb-12">
            <RevealText
              as="h2"
              text={"Puterea comunității,\noriunde ai nevoie de ea."}
              highlight={["comunității"]}
              className="font-bold text-text tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)]"
            />
            <Reveal delay={0.12}>
              <p className="mt-6 text-lg sm:text-xl text-text-light leading-relaxed">
                De la cumpărături și ieșiri în oraș până la călătorii.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 auto-rows-[150px] sm:auto-rows-[220px] gap-3 sm:gap-4">
            {CATEGORY_TILES.map((category, index) => (
              <div
                key={category.key}
                className={`group relative overflow-clip rounded-[1.4rem] sm:rounded-3xl bg-surface ${category.className}`}
              >
                <RevealImage
                  name={category.image}
                  ratio="auto"
                  delay={index * 0.08}
                  sizes="(max-width: 640px) 100vw, 50vw"
                  className="h-full w-full"
                  imgClassName={`${category.imageClassName} transition-[filter] duration-700 ease-out group-hover:saturate-[1.08]`}
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent"
                />
                <span className="absolute bottom-4 left-4 sm:bottom-5 sm:left-5 text-white font-semibold text-base sm:text-lg [text-shadow:0_1px_14px_rgba(0,0,0,0.45)]">
                  {category.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SIGNUP - foto full bleed ============ */}
      <section
        id="signup"
        className="relative isolate overflow-clip bg-accent scroll-mt-24"
      >
        <RevealImage
          name="suuper-card-usage"
          priority
          fill
          sizes="100vw"
          imgClassName="object-[58%_42%]"
        />
        {/* Fotografia rămâne luminoasă; scrim-ul discret ajută doar textul alb. */}
        <div aria-hidden="true" className="absolute inset-0 bg-accent/5" />
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-r from-accent/25 via-accent/5 to-transparent"
        />
        <div aria-hidden="true" className="signup-progressive-blur" />

        <div className="relative container mx-auto max-w-6xl px-4 py-24 sm:py-32">
          <div className="grid lg:grid-cols-[1fr_0.85fr] gap-14 lg:gap-20 items-center">
            {/* Pe mobil coloana e singură pe lățime, deci se centrează;
                de la lg, unde stă lângă formular, revine aliniată la stânga. */}
            <div className="max-w-xl mx-auto text-center lg:mx-0 lg:text-left">
              <p className="text-[11px] font-bold tracking-[0.14em] text-white/90 mb-5">
                ACCES ÎNAINTE DE LANSARE
              </p>

              <RevealText
                as="h2"
                text={"Înscrie-te în clubul suuper."}
                className="font-bold text-white tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)]"
              />

              <Reveal delay={0.15}>
                <p className="mt-6 text-lg sm:text-xl text-white/90 leading-relaxed max-w-xl mx-auto lg:mx-0">
                  Lasă-ne emailul. Primești acces înaintea lansării și afli
                  primul când comunitatea deblochează un preț mai bun.
                </p>

                <ul className="mt-8 space-y-3 inline-block text-left lg:block">
                  {SIGNUP_PROMISES.map((promise) => (
                    <li
                      key={promise}
                      className="flex items-center gap-3 text-white"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15">
                        <Check size={13} weight="bold" className="text-white" />
                      </span>
                      {promise}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="flex justify-center lg:justify-end">
              <WaitlistForm
                source="landing-main"
                isStudent={isStudent}
                onIsStudentChange={setIsStudent}
                referredBy={ref}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============ CUM FUNCȚIONEAZĂ ============ */}
      <section
        id="cum-functioneaza"
        className="py-20 sm:py-28 px-4 bg-background scroll-mt-20"
      >
        <div className="container mx-auto max-w-6xl">
          <RevealText
            as="h2"
            text={"Trei pași\nși plătești mai puțin."}
            highlight={["puțin."]}
            className="text-center font-bold text-text tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)] mx-auto max-w-3xl"
          />

          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {HOW_IT_WORKS.map((item, index) => (
              <Reveal
                key={item.step}
                delay={index * 0.1}
                className="rounded-[28px] bg-white border border-border p-7 text-center sm:p-8 lg:text-left"
              >
                <p className="text-sm font-bold tracking-widest text-primary mb-5">
                  {item.step}
                </p>
                <p className="text-2xl font-bold text-text mb-3">{item.title}</p>
                <p className="text-text-light leading-relaxed">{item.text}</p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <p className="mt-12 text-center text-lg sm:text-xl font-semibold text-text max-w-2xl mx-auto">
              Fără puncte. Fără 17 niveluri de loialitate. Prețul mai mic e
              recompensa.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ SUUPER PAY ============ */}
      <SuuperPaySection />

      {/* ============ GROUP UNLOCK ============ */}
      <section
        id="prag-comunitate"
        className="py-20 sm:py-28 px-4 overflow-clip scroll-mt-20"
      >
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-16 items-center">
            <RevealImage
              name="friends-group-steps-phone"
              ratio="4/5"
              sizes="(max-width: 1024px) 90vw, 440px"
              className="rounded-[32px] shadow-xl mx-auto w-full max-w-[420px] lg:max-w-none order-2 lg:order-1"
            />

            <div className="order-1 lg:order-2 text-center lg:text-left">
              <SectionHeading
                align="responsive"
                eyebrow="PRAG DE COMUNITATE"
                title="Cu fiecare membru, prețul poate coborî."
                subtitle="Unele prețuri se activează când atingem un prag. Când ajungem acolo, negociem din nou — aceeași ofertă mai bună, pentru toată comunitatea."
              />

              <div className="mt-9 flex justify-center lg:justify-start">
                <GroupUnlockCard
                  members={stats?.members}
                  target={stats?.target ?? 10000}
                  onInvite={() => scrollToSignup()}
                />
              </div>

              <Reveal delay={0.1}>
                <p className="mt-8 text-lg font-semibold text-text text-center lg:text-left">
                  La {formatNumber(stats?.target ?? 10000)} de membri, negociem
                  din nou pentru toată comunitatea.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============ STUDENT MODE ============ */}
      <section className="py-20 sm:py-28 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <SectionHeading
                align="responsive"
                eyebrow="PENTRU STUDENȚI"
                title="Student? Pornești cu un avantaj."
                subtitle="Îți verificăm statutul o singură dată. Apoi vezi automat prețurile și pragurile create pentru studenți."
              />

              <button
                type="button"
                onClick={() => scrollToSignup({ asStudent: true })}
                className="join-cta mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-gradient text-white rounded-2xl font-semibold text-lg shadow-lg shadow-primary/25"
              >
                Intră în suuper club ca student
                <ArrowRight size={20} weight="bold" />
              </button>

              <p className="mt-4 text-sm text-text-muted">
                Fără card. Confirmarea statutului se activează la lansare.
              </p>
            </div>

            {/* Foto cu badge-ul de student suprapus */}
            <div className="relative pb-16 sm:pb-8">
              <RevealImage
                name="woman-purple-hoodie-phone"
                ratio="4/3"
                sizes="(max-width: 1024px) 90vw, 520px"
                className="rounded-[32px] shadow-xl"
              />

              <Reveal
                delay={0.25}
                className="absolute -bottom-0 left-4 right-4 sm:left-auto sm:-right-4 sm:bottom-4 sm:w-[240px]"
              >
                <div className="rounded-3xl bg-white/90 backdrop-blur-md border border-white/60 shadow-2xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <GraduationCap size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold tracking-[0.14em] text-primary">
                        STATUT DE STUDENT
                      </p>
                      <p className="text-base font-bold text-text leading-tight">
                        Verificat
                      </p>
                    </div>
                  </div>

                  <ul className="space-y-2">
                    {["Acces prioritar", "Prețuri pentru studenți", "Prețuri de grup"].map(
                      (item) => (
                        <li
                          key={item}
                          className="flex items-center gap-2.5 text-sm text-text"
                        >
                          <span
                            aria-hidden="true"
                            className="w-1.5 h-1.5 rounded-full bg-primary"
                          />
                          {item}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SUUPER PRICE / EXEMPLE ============ */}
      <section className="py-20 sm:py-28 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mx-auto max-w-3xl">
            <p className="text-[11px] font-bold tracking-[0.14em] text-primary mb-4">
              SUUPER PRICE
            </p>
            <RevealText
              as="h2"
              text="Un preț negociat pentru membrii suuper."
              highlight={["suuper."]}
              className="font-bold text-text tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)]"
            />
            <Reveal delay={0.15}>
              <p className="mt-6 text-lg sm:text-xl text-text-light leading-relaxed">
                Fără cupoane căutate pe internet. Vezi direct prețul obținut
                pentru comunitate.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {demoOffers.map((offer, index) => (
              // Pe mobil cardul e un rând compact: categorie + prețuri în
              // stânga, economia în dreapta. De la sm în sus revine stivuit.
              <Reveal
                key={offer.category}
                delay={index * 0.1}
                className="flex items-center justify-between gap-4 rounded-3xl bg-white border border-border shadow-lg p-5 sm:block sm:rounded-[28px] sm:p-7"
              >
                <div className="min-w-0">
                  <p className="text-base sm:text-xl font-bold text-text sm:mb-6">
                    {offer.category}
                  </p>
                  <div className="mt-1 flex items-baseline gap-2 sm:mt-0 sm:block">
                    <p className="text-text-muted line-through text-sm sm:text-lg">
                      {formatNumber(offer.normalPrice)} lei
                    </p>
                    <p className="text-2xl sm:text-4xl font-bold text-primary tracking-tight sm:mt-1">
                      {formatNumber(offer.memberPrice)} lei
                    </p>
                  </div>
                </div>

                <p className="shrink-0 inline-flex items-center rounded-full bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary sm:mt-4">
                  −{formatNumber(offerSaving(offer))} lei
                </p>
              </Reveal>
            ))}
          </div>

          <p className="mt-8 text-center text-sm text-text-muted max-w-2xl mx-auto">
            Exemple demonstrative. Ofertele reale apar în aplicație pe măsură ce
            se activează parteneriatele.
          </p>
        </div>
      </section>

      {/* ============ BANDĂ FOTO ============ */}
      <section className="py-16 sm:py-20 overflow-hidden">
        <RevealText
          as="h2"
          text={"Nu e un club exclusivist.\nE fix invers."}
          highlight={["invers."]}
          className="px-4 text-center font-bold text-text tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)] mb-10 mx-auto max-w-3xl"
        />
        <PhotoMarquee images={MARQUEE_IMAGES} durationSeconds={70} />
      </section>

      {/* ============ DE CE OFERĂ COMPANIILE REDUCERI ============ */}
      <section className="py-20 sm:py-28 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <SectionHeading
            title="Brandurile câștigă volum. Membrii, un preț mai bun."
            subtitle="suuper adună cererea într-un singur loc. Brandul oferă un preț de membru, comunitatea răspunde — fără costul de a câștiga fiecare client separat."
          />

          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {[
              {
                icon: Storefront,
                label: "BRAND",
                text: "Pune pe masă un preț care merită atenția comunității.",
              },
              {
                icon: UsersThree,
                label: "SUUPER",
                text: "Adună cererea și negociază în numele comunității.",
              },
              {
                icon: ShoppingCart,
                label: "MEMBRI",
                text: "Văd prețul direct și decid simplu dacă merită pentru ei.",
              },
            ].map((item, index) => (
              // Pe mobil iconița stă lângă text, nu deasupra: trei carduri
              // înalte una sub alta ocupau tot ecranul degeaba.
              <Reveal
                key={item.label}
                delay={index * 0.1}
                className="flex items-start gap-4 rounded-3xl bg-white border border-border p-5 sm:block sm:rounded-[28px] sm:p-7"
              >
                <div className="w-11 h-11 shrink-0 sm:w-12 sm:h-12 rounded-2xl bg-primary/10 flex items-center justify-center sm:mb-5">
                  <item.icon size={22} className="text-primary" />
                </div>

                <div className="min-w-0">
                  <p className="text-[11px] font-bold tracking-[0.14em] text-primary mb-2 sm:mb-3">
                    {item.label}
                  </p>
                  <p className="text-text-light leading-relaxed">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ / CLARITATE DE POZIȚIONARE ============ */}
      <section
        id="faq"
        className="py-20 sm:py-28 px-4 bg-white scroll-mt-20"
      >
        <div className="container mx-auto max-w-6xl">
          <div className="grid items-start gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-20">
            <div className="text-center lg:sticky lg:top-28 lg:text-left">
              <RevealText
                as="h2"
                text={"Ce este suuper.\nȘi ce nu este."}
                highlight={["suuper."]}
                className="font-bold text-text tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)]"
              />
              <Reveal delay={0.12}>
                <p className="mt-6 max-w-md mx-auto lg:mx-0 text-lg sm:text-xl text-text-light leading-relaxed">
                  Întrebările pe care le-ai pune oricum, cu răspunsuri directe.
                  Fără asteriscuri și fără condiții ascunse.
                </p>

                {/* Cardul rămâne aliniat la stânga: e o listă, nu un titlu */}
                <div className="mt-8 rounded-[28px] border border-primary/15 bg-white p-6 text-left shadow-[0_18px_50px_rgba(70,58,190,0.08)] sm:p-7">
                  <p className="text-[11px] font-bold tracking-[0.14em] text-primary mb-5">
                    PE SCURT
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 text-text-light">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500">
                        <X size={14} weight="bold" aria-hidden="true" />
                      </span>
                      <p>Nu cauți reduceri.</p>
                    </div>
                    <div className="flex items-start gap-3 font-semibold text-text">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Check size={14} weight="bold" aria-hidden="true" />
                      </span>
                      <p>Intri într-o comunitate care negociază mai bine.</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="space-y-3">
              {FAQ_ITEMS.map((item) => (
                <details
                  key={item.question}
                  className="group rounded-[24px] border border-border bg-white px-5 transition-[border-color,box-shadow] duration-300 open:border-primary/25 open:shadow-[0_16px_44px_rgba(22,24,45,0.07)] hover:border-primary/20 sm:px-7"
                >
                  <summary className="flex min-h-16 cursor-pointer list-none items-center justify-between gap-5 py-5 text-left [&::-webkit-details-marker]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 rounded-xl">
                    <span className="text-lg sm:text-xl font-bold text-text leading-snug">
                      {brandify(item.question)}
                    </span>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background text-text transition-[transform,color,background-color] duration-300 group-open:rotate-45 group-open:bg-primary/10 group-open:text-primary">
                      <Plus size={18} weight="bold" aria-hidden="true" />
                    </span>
                  </summary>
                  <p className="pb-6 pr-12 text-base sm:text-lg text-text-light leading-relaxed">
                    {brandify(item.answer)}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ NOUTĂȚI ============ */}
      <section className="bg-background px-4 py-20 sm:py-28">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <RevealText
              as="h2"
              text={"Noutăți din suuper club."}
              highlight={["suuper"]}
              className="font-bold text-text tracking-tight leading-[1.05] text-[clamp(2rem,5.5vw,3.25rem)]"
            />

            <Link
              to="/noutati"
              className="shrink-0 font-semibold text-primary hover:underline"
            >
              Toate noutățile →
            </Link>
          </div>

          <ul className="mt-12 grid gap-5 md:grid-cols-3">
            {articles.slice(0, 3).map((article, index) => (
              <li key={article.slug}>
                <Link
                  to="/noutati/$slug"
                  params={{ slug: article.slug }}
                  viewTransition
                  className="group flex h-full flex-col overflow-hidden rounded-[28px] border border-border bg-white shadow-sm transition-shadow hover:shadow-xl"
                >
                  {/* Același nume pe cardul de aici și pe imaginea mare din
                      articol: browserul le recunoaște ca fiind aceeași
                      fotografie și o transformă dintr-una în alta. */}
                  <RevealImage
                    name={article.image}
                    ratio="16/9"
                    sizes="(max-width: 768px) 100vw, 360px"
                    delay={index * 0.08}
                    parallax={false}
                    viewTransitionName={`article-${article.slug}`}
                    className="w-full"
                    imgClassName="transition-transform duration-700 group-hover:scale-105"
                  />

                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-sm text-text-muted">
                      {formatArticleDate(article.publishedAt)} ·{" "}
                      {article.readMinutes} min
                    </p>
                    <p className="mt-2 text-xl font-bold leading-tight text-text">
                      {brandify(article.title)}
                    </p>
                    <p className="mt-3 flex-1 text-text-light leading-relaxed">
                      {brandify(article.description)}
                    </p>
                    <p className="mt-4 font-semibold text-primary">Citește →</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ CTA-URI FINALE - două carduri foto ============ */}
      <section className="bg-white px-4 pt-4 pb-20 sm:pt-6 sm:pb-28">
        <div className="container mx-auto max-w-7xl grid gap-4 lg:grid-cols-2">
          <article id="pentru-business" className="scroll-mt-24">
            <RevealImage
              name="suuper-card-usage"
              ratio="auto"
              sizes="(max-width: 1024px) 100vw, 50vw"
              imgClassName="object-[56%_center]"
              className="min-h-[780px] sm:min-h-[760px] lg:min-h-[680px] rounded-[32px] shadow-lg"
            >
              <div aria-hidden="true" className="absolute inset-0 bg-black/10" />
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-[78%] bg-gradient-to-t from-[#0d1020]/95 via-[#0d1020]/55 via-55% to-transparent"
              />
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-[66%] backdrop-blur-[12px] [mask-image:linear-gradient(to_top,black_0%,black_28%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_top,black_0%,black_28%,transparent_100%)]"
              />
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-[34%] backdrop-blur-[22px] [mask-image:linear-gradient(to_top,black_0%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_top,black_0%,transparent_100%)]"
              />

              <div className="absolute inset-0 flex items-end p-7 sm:p-10 lg:p-12">
                <div className="w-full max-w-xl text-center lg:text-left">
                  <p className="mb-5 inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[11px] font-bold tracking-[0.14em] text-white/80 backdrop-blur-md">
                    PENTRU BUSINESS
                  </p>

                  <RevealText
                    as="h2"
                    text={"Ai un brand?\nNoi aducem comunitatea."}
                    className="font-bold text-white tracking-tight leading-[1.04] text-[clamp(2rem,4vw,3rem)]"
                  />

                  <Reveal delay={0.15}>
                    <p className="mt-6 text-lg text-white/75 leading-relaxed max-w-xl">
                      Tu pui prețul de membru. Noi aducem o comunitate care
                      caută motive reale să te aleagă.
                    </p>

                    <Link
                      to="/pentru-business"
                      className="join-cta mt-8 w-full sm:w-auto inline-flex min-h-12 items-center justify-center gap-2 px-8 py-4 bg-white text-text rounded-2xl border border-white/80 font-semibold text-lg shadow-[0_14px_34px_rgba(0,0,0,0.22)]"
                    >
                      Devino partener
                      <ArrowRight size={20} weight="bold" />
                    </Link>

                    <p className="mt-4 text-sm text-white/55">
                      Scrie-ne la {PARTNER_EMAIL}
                    </p>
                  </Reveal>
                </div>
              </div>
            </RevealImage>
          </article>

          <article>
            <RevealImage
              name="couple-laughing-street"
              ratio="auto"
              sizes="(max-width: 1024px) 100vw, 50vw"
              imgClassName="object-[48%_center]"
              className="min-h-[780px] sm:min-h-[760px] lg:min-h-[680px] rounded-[32px] shadow-lg"
            >
              <div aria-hidden="true" className="absolute inset-0 bg-black/10" />
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-[78%] bg-gradient-to-t from-[#0d1020]/95 via-[#0d1020]/55 via-55% to-transparent"
              />
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-[66%] backdrop-blur-[12px] [mask-image:linear-gradient(to_top,black_0%,black_28%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_top,black_0%,black_28%,transparent_100%)]"
              />
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-[34%] backdrop-blur-[22px] [mask-image:linear-gradient(to_top,black_0%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_top,black_0%,transparent_100%)]"
              />

              <div className="absolute inset-0 flex items-end p-7 sm:p-10 lg:p-12">
                <div className="w-full max-w-xl text-center lg:text-left">
                  <p className="mb-5 inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[11px] font-bold tracking-[0.14em] text-white/80 backdrop-blur-md">
                    ACCES ÎNAINTE DE LANSARE
                  </p>

                  <RevealText
                    as="h2"
                    text={"Singur ești un client.\nÎmpreună avem putere de cumpărare."}
                    className="font-bold text-white tracking-tight leading-[1.04] text-[clamp(2rem,4vw,3rem)]"
                  />

                  <Reveal delay={0.2}>
                    <p className="mt-6 text-lg text-white/75 leading-relaxed max-w-xl">
                      Înscrie-te în clubul suuper. Fiecare membru nou ne apropie de
                      următorul preț deblocat.
                    </p>

                    <button
                      type="button"
                      onClick={() => scrollToSignup()}
                      className="join-cta mt-8 w-full sm:w-auto inline-flex min-h-12 items-center justify-center gap-2 px-8 py-4 bg-primary-gradient text-white rounded-2xl font-semibold text-lg shadow-[0_14px_34px_rgba(28,19,105,0.4)]"
                    >
                      Intră în suuper club
                      <ArrowRight size={20} weight="bold" />
                    </button>

                    <p className="mt-4 text-sm text-white/55">
                      Gratuit în perioada de lansare. Fără card.
                    </p>
                  </Reveal>
                </div>
              </div>
            </RevealImage>
          </article>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-12 px-4 bg-primary rounded-t-[32px] sm:rounded-t-[44px]">
        <div className="container mx-auto max-w-6xl flex flex-col md:flex-row items-center justify-between gap-6">
          <img
            src="/suuper_logo_white.svg"
            alt="suuper"
            className="h-7 w-auto"
          />

          <nav
            // Pe mov, alb/60 scade sub 4.5:1; /85 trece pragul AA
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/85"
            aria-label="Linkuri footer"
          >
            <Link to="/pentru-business" className="hover:text-white transition-colors">
              Pentru business
            </Link>
            <Link to="/noutati" className="hover:text-white transition-colors">
              Noutăți
            </Link>
            <Link to="/confidentialitate" className="hover:text-white transition-colors">
              Confidențialitate
            </Link>
            <Link to="/termeni" className="hover:text-white transition-colors">
              Termeni
            </Link>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="hover:text-white transition-colors"
            >
              Setări cookie-uri
            </button>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="hover:text-white transition-colors"
            >
              Contact
            </a>
            <span className="text-white/70">
              Imagini ilustrative generate cu AI
            </span>
            <span className="text-white/80">© suuper</span>
          </nav>
        </div>
      </footer>
    </div>
  );
}
